<template>
  <v-container class="px-5 fill-height d-flex justify-center">
    <div class="login-div mx-lg-auto">
      <v-card class="login-card px-6 py-6" outlined>
        <!-- Header -->
        <div class="d-flex align-center justify-space-between mb-4">
          <h1 class="mb-0 primary--text text-h5 text-md-h4">{{ $t('login.cert_sign_in') }}</h1>
        </div>

        <!-- Alert -->
        <v-alert v-if="error" class="mb-4" border="left" type="error" dense>
          {{ errorMsg }}
        </v-alert>

        <v-alert
          v-if="certServiceOk === false && certServiceErrorCode === 500"
          border="left"
          icon="mdi-information-outline"
          class="mb-4 ps-blue-alert"
        >
          {{ $t('login.cert_plugin_required') || 'Para usar tu certificado digital en PortaSigma necesitas instalar PortaSigma Web Plugin' }}

          <div class="mt-2 d-flex justify-center">
            <v-btn
              small
              :style="{ backgroundColor: buttonColor, color: buttonTextColor }"
              :href="pluginDownloadUrl"
              target="_blank"
              rel="noopener"
              download
            >
              <v-icon left small>mdi-download</v-icon>
              {{ 'Windows 64-bit' }}
            </v-btn>
          </div>

          <div class="mt-3 note">
            {{ $t('login.java_required') || 'PortaSigma Web Plugin requiere tener instalado Java' }}
          </div>
        </v-alert>

        <!-- Form -->
        <v-form ref="form" v-model="valid" @submit.prevent="login">
          <!-- Controls row -->
          <v-row no-gutters class="mb-2">
            <v-col cols="12" class="text-right">
              <v-btn text small :disabled="loading" @click="fetchCertificates" class="px-0">
                <v-icon left small>mdi-refresh</v-icon>
                {{ $t('login.search_certs') || 'Buscar certificados' }}
              </v-btn>
            </v-col>
          </v-row>

          <!-- Selector con coloreado por validez (oculto si servicio 500) -->
          <v-select
            v-if="!(certServiceOk === false && certServiceErrorCode === 500)"
            dense outlined required persistent-placeholder
            :rules="selectionRules"
            :items="certOptions"
            item-text="label"
            item-value="id"
            v-model="selectedCertId"
            :placeholder="$t('login.select_certificate') || 'Selecciona un certificado'"
            :hint="$t('login.cert_hint') || 'Elige el certificado con el que vas a firmar'"
            persistent-hint
            prepend-inner-icon="mdi-certificate"
            class="mb-3"
          >
            <!-- Render de cada opción del menú -->
            <template v-slot:item="data">
              <v-list-item v-bind="data.attrs" v-on="data.on" :class="optionClass(data.item)">
                <v-list-item-content>
                  <v-list-item-title class="text-body-2">{{ data.item.subjectCN || data.item.subject }}</v-list-item-title>
                  <v-list-item-subtitle class="caption">
                    {{ data.item.issuer }}
                    <span v-if="!isCertValidByItem(data.item)"> · {{ invalidShortMessage(data.item) }}</span>
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-icon>
                  <v-icon small>{{ isCertValidByItem(data.item) ? 'mdi-check-circle-outline' : 'mdi-alert-circle-outline' }}</v-icon>
                </v-list-item-icon>
              </v-list-item>
            </template>

            <!-- Render del valor seleccionado (chip coloreado) -->
            <template v-slot:selection="data">
              <div :class="['selection-pill', isCertValidByItem(data.item) ? 'opt-valid' : 'opt-invalid']">
                <span class="truncate">{{ data.item.subjectCN || data.item.subject }} — {{ data.item.issuer }}</span>
              </div>
            </template>
          </v-select>

          <!-- Detalle del certificado (oculto si servicio 500) -->
          <v-expand-transition>
            <v-card
              v-if="selected && !(certServiceOk === false && certServiceErrorCode === 500)"
              flat
              class="mb-3 pa-3 grey lighten-5 rounded"
            >
              <div class="font-weight-medium">{{ selected.subjectCN || selected.subject }}</div>
              <div class="caption grey--text">{{ selected.subject }}</div>
              <v-divider class="my-2" />
              <div class="d-flex flex-column caption mb-2">
                <div class="mb-1"><strong>{{ $t('login.issuer') || 'Emisor' }}:</strong> {{ selected.issuer }}</div>
                <div><strong>{{ $t('login.to') || 'Vencimiento' }}:</strong> {{ fmt(selected.notAfter) }}</div>
              </div>

              <!-- Motivo si NO es válido -->
              <v-alert v-if="!isCertValid(selected)" type="error" outlined dense class="mb-0">
                <div class="font-weight-medium">{{ $t('login.invalid_cert') || 'Certificado no válido' }}</div>
                <div class="caption">{{ invalidReasonMessage(selected) }}</div>
              </v-alert>
            </v-card>
          </v-expand-transition>

          <!-- Submit -->
          <!-- Identificador de acceso (centrado, depende de certificado seleccionado) -->
          <div
            v-if="selected && Array.isArray(selected.ids) && selected.ids.length"
            class="mb-4 d-flex flex-column align-center"
          >
            <div class="text-center font-weight-medium mb-2">
              {{ $t('login.access_identifier') || 'Identificador de acceso' }}
            </div>

            <v-select
              dense
              outlined
              hide-details="auto"
              class="mx-auto"
              style="max-width: 360px; width: 100%;"
              :items="availableIds"
              v-model="accessId"
              :rules="idSelectionRules"
              :placeholder="$t('login.select_id') || 'Selecciona un identificador'"
              persistent-placeholder
            />
          </div>

          <v-btn
            small
            type="submit"
            :style="{
              backgroundColor: buttonColor,
              color: buttonTextColor,
              height: '44px',
              width: '100%'
            }"
            :disabled="!selectedCertId || loading || (certServiceOk === false && certServiceErrorCode === 500)"
          >
            <h3 class="font-weight-regular mb-0">
              {{ $t('login.login') }}
            </h3>
          </v-btn>
        </v-form>
      </v-card>
    </div>

    <DefaultLoading v-if="loading" :loading="loading" />
  </v-container>
</template>

<script>
import { SignaturesService } from "../../services/signatures-service";



export default {
  name: 'CertLogin',
  props: {
    buttonColor: { type: String, default: '#1976D2' },
    buttonTextColor: { type: String, default: '#fff' },
  },
  data() {
    return {
      valid: true,
      error: false,
      errorMsg: '',
      loading: false,
      accessId: null,
      idSelectionRules: [v => !!v || (this.$t('login.select_id') || 'Selecciona un identificador')],

      // ⬇️ estado del servicio de certificados
      certServiceOk: null,        // null: desconocido, true: OK, false: KO
      certServiceErrorCode: null, // ej. 500

      certs: [
        /* {
          id: 'mock-1',
          subject: 'CN=Juan Pérez, O=Ciudadano, C=ES',
          subjectCN: 'Juan Pérez',
          issuer: 'AC FNMT Usuarios',
          notAfter: new Date('2026-04-10'),
          status: 'valid',
          type: 'Persona física',
        },
        {
          id: 'mock-2',
          subject: 'CN=María García, O=Empresa XYZ, C=ES',
          subjectCN: 'María García',
          issuer: 'AC Camerfirma',
          notAfter: new Date('2025-11-01'),
          status: 'valid',
          type: 'Representante de empresa',
        },
        {
          id: 'mock-4',
          subject: 'CN=Ana López, O=Empresa ACME, C=ES',
          subjectCN: 'Ana López',
          issuer: 'AC FNMT Usuarios',
          notAfter: new Date('2024-05-15'),
          status: 'revoked',
          invalidReason: 'Revocado por OCSP el 12/07/2025',
          type: 'Persona física',
        },*/


      ],
      selectedCertId: 'mock-1',
      selectionRules: [v => !!v || (this.$t('login.select_certificate') || 'Selecciona un certificado')],
    };
  },

  watch: {
    // ✅ Cada vez que cambie el certificado seleccionado, preselecciona primaryId
    selected: {
      immediate: true,
      handler(n) {
        if (n) {
          this.accessId = n.primaryId || (Array.isArray(n.ids) ? n.ids[0] : null);
        } else {
          this.accessId = null;
        }
      }
    }
  },

  async mounted() {
    await this.checkCertService(); // chequeo inicial
    console.log("Service OK? " + this.certServiceOK);
    if (this.certServiceOK!==false) this.loadCertificates();  //Si el Web Plugin está disponible, cargar certificados.
  },

  computed: {
    certOptions() {
      return this.certs.map(c => ({ ...c, label: `${c.subjectCN || c.subject} — ${c.issuer}` }));
    },
    selected() {
      return this.certs.find(c => c.id === this.selectedCertId) || null;
    },
    availableIds() {
      // Devuelve un array de strings con los IDs del cert seleccionado
      const ids = (this.selected && Array.isArray(this.selected.ids)) ? this.selected.ids : [];
      return ids;
    },

  },
  methods: {
    // ⬇️ comprueba el servicio; si 500, mostramos la alerta
    async checkCertService() {
      try {
        const res = await SignaturesService.pingCert(2000);
        this.certServiceOk = res.status >= 200 && res.status < 300;
        console.log("certServiceOK: " + this.certServiceOK);
        this.certServiceErrorCode = this.certServiceOk ? null : res.status;
        // logs opcionales:
        console[this.certServiceOk ? 'log' : 'warn']('ping:', res.status, res.data ?? res.statusText);
      } catch (e) {
        // timeout o error de red: no es 500, pero puedes decidir qué hacer
        this.certServiceOk = false;
        this.certServiceErrorCode = 0;
        // console.error('ping error:', e.message || e);
      }
    },
    async loadCertificates() {
      this.loading = true;
      this.error = false;
      try {
        const { status, certificates } = await SignaturesService.getCertificatesSummaries(4000);
        if (status >= 200 && status < 300) {
          const now = Date.now();

          this.certs = certificates.map(c => {
            const { ids, cnId, serialId, primaryId } =
              SignaturesService.extractCertificateIds(c, { prefer: 'cn', numberOnly: false });

            const notAfterTs = c.notAfter ? new Date(c.notAfter).getTime() : null;
            const expired = notAfterTs !== null && notAfterTs < now;

            return {
              ...c,
              ids, cnId, serialId, primaryId,
              type: c.type || 'Desconocido',
              status: expired ? 'invalid' : (c.status || 'valid'),
              invalidReason: expired ? `Caducado el ${this.fmt(c.notAfter)}` : c.invalidReason
            };
          });

          this.certs.sort((a, b) =>
            (a.status === 'valid') === (b.status === 'valid') ? 0 : (a.status === 'valid' ? -1 : 1)
          );

          // ⬇️ LOG de IDs (para todas las entradas)
                console.table(this.certs.map(c => ({
                  id: c.id,
                  subjectCN: c.subjectCN,
                  cnId: c.cnId,
                  serialId: c.serialId,
                  primaryId: c.primaryId,
                  ids: (c.ids || []).join(', ')
                })));

          if (!this.selectedCertId && this.certs.length) {
            this.selectedCertId = this.certs[0].id;
            console.log('IDs:', this.certs[0].ids, 'primaryId:', this.certs[0].primaryId);
          }

          // ✅ redundante pero seguro: fija accessId tras la carga
              if (this.selected) {
                this.accessId = this.selected.primaryId || (Array.isArray(this.selected.ids) ? this.selected.ids[0] : null);
              }

          if (!this.certs.length) {
            this.error = true;
            this.errorMsg = this.$t('login.no_certs') || 'No se han encontrado certificados';
          }
        } else {
          this.error = true;  // Error 500
          this.errorMsg =  this.$t('login.no_certs') || 'Error obteniendo certificados';
        }
      } catch (err) {
        this.error = true;
        this.errorMsg = String(err?.message || err);
      } finally {
        this.loading = false;
      }
    },


    fmt(d) {
      try { return (typeof d === 'string' ? new Date(d) : d).toLocaleDateString(); }
      catch (_) { return String(d); }
    },
    isCertValid(cert) {
      if (!cert) return false;
      if (String(cert.status).toLowerCase() === 'revoked' || String(cert.status).toLowerCase() === 'invalid') return false;
      if (cert.notAfter && new Date(cert.notAfter).getTime() < Date.now()) return false;
      return true;
    },
    isCertValidByItem(item) { return this.isCertValid(item); },
    invalidReasonMessage(cert) {
      if (!cert) return '';
      if (cert.invalidReason) return cert.invalidReason;
      if (cert.notAfter && new Date(cert.notAfter).getTime() < Date.now()) {
        return this.$t('login.expired_on', { date: this.fmt(cert.notAfter) }) || `Caducado el ${this.fmt(cert.notAfter)}`;
      }
      if (String(cert.status).toLowerCase() === 'revoked') return this.$t('login.revoked') || 'Revocado';
      return this.$t('login.not_valid') || 'No válido';
    },
    invalidShortMessage(item) { return this.invalidReasonMessage(item); },
    optionClass(item) { return this.isCertValidByItem(item) ? 'opt-valid' : 'opt-invalid'; },

    async fetchCertificates() {
      this.loading = true;
      this.error = false;
      await this.checkCertService();
      if (this.certServiceOk === false && this.certServiceErrorCode === 500) {
        this.loading = false;
        this.error = true;  // Error 500
        this.errorMsg =  this.$t('login.no_certs') || 'Error obteniendo certificados';
        return; // no seguimos: mostramos alerta
      }
      setTimeout(() => {
        if (!this.selectedCertId && this.certs.length) this.selectedCertId = this.certs[0].id;
        this.loading = false;
      }, 500);
      this.loadCertificates();
    },

    login() {
      // bloquea si el servicio está en 500
      if (this.certServiceOk === false && this.certServiceErrorCode === 500) {
        this.error = true;
        this.errorMsg = this.$t('login.cert_plugin_required') || 'Para usar tu certificado digital en PortaSigma necesitas instalar PortaSigma Web Plugin';
        return;
      }
      if (!this.selectedCertId) {
        this.error = true;
        this.errorMsg = this.$t('login.select_certificate') || 'Selecciona un certificado';
        return;
      }
      this.loading = true;
      this.error = false;
      const cert = this.selected;
      setTimeout(() => {
        const payload = {
          token: 'mock-token-123',
          subjectCN: cert.subjectCN,
          subject: cert.subject,
          issuer: cert.issuer,
          validUntil: cert.notAfter,
          status: cert.status,
          type: cert.type,
        };
        this.$emit('success', payload);
        this.loading = false;
      }, 800);
    },
  },
};
</script>

<style scoped>
.login-div { max-width: 520px; width: 100%; }
.login-card { border-radius: 14px; }
@media (max-width: 600px) { .login-div { max-width: 100%; } }

/* Colores de validez en opciones */
.opt-valid { background-color: #eaf7ee !important; }
.opt-invalid { background-color: #fdecea !important; }
.selection-pill {
  border-radius: 12px;
  padding: 2px 6px;
  display: inline-flex;
  max-width: 100%;
}
.truncate { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
</style>
